/**
 * The pure, per-voxel half of the segmentation overlay conversion.
 *
 * Kept free of vtk.js imports on purpose: this module is loaded by the
 * neuroimage decode worker (#670), where the conversion runs so it no longer
 * blocks the analysis page, and where vtk.js has no business being — it is not
 * worker-safe and would bloat the worker bundle. The vtk.js objects are built
 * from this output on the main thread, in `segmentation_utils.ts`.
 */

/** Label-ID volume plus the palette needed to color it. */
export interface SegLabels {
    /** 1-component label IDs, one per voxel (0 = background). */
    labels: Uint8Array | Uint16Array;
    /** Label ID → RGB (0-255). Index 0 is the reserved background entry. */
    palette: Array<[number, number, number]>;
    /** Highest label ID present; anchors the piecewise-opacity endpoint. */
    maxLabelId: number;
}

/**
 * Convert backend-provided RGBA segmentation into label IDs plus a palette.
 * Voxels with alpha=0 become label 0 (background) regardless of RGB; unique
 * (R,G,B) triplets among alpha>0 voxels get incrementing IDs.
 *
 * O(n) over the RGBA array with a Map lookup per voxel — on a 256³ volume this
 * is 16M iterations, which is exactly why it belongs off the main thread.
 *
 * @param segRgba Flat RGBA bytes, 4 per voxel, in voxel-major order.
 */
export function rgbaToSegLabels(segRgba: Uint8Array): SegLabels {
    const voxelCount = segRgba.length / 4;
    // Start with Uint8Array; if we overflow 255 labels, switch to Uint16Array.
    let labels: Uint8Array | Uint16Array = new Uint8Array(voxelCount);
    let usingUint16 = false;
    const colorMap = new Map<number, number>(); // packed RGB → label ID
    // Entry 0 reserved for background.
    let nextId = 1;
    const palette: Array<[number, number, number]> = [[0, 0, 0]];

    for (let v = 0; v < voxelCount; v++) {
        const i = v * 4;
        const a = segRgba[i + 3]!;
        if (a === 0) {
            // labels[v] already 0 from typed-array zero-init
            continue;
        }
        const r = segRgba[i]!;
        const g = segRgba[i + 1]!;
        const b = segRgba[i + 2]!;
        const key = (r << 16) | (g << 8) | b;
        let id = colorMap.get(key);
        if (id === undefined) {
            id = nextId++;
            colorMap.set(key, id);
            palette.push([r, g, b]);
            if (!usingUint16 && id > 255) {
                const upgraded = new Uint16Array(voxelCount);
                upgraded.set(labels);
                labels = upgraded;
                usingUint16 = true;
            }
        }
        labels[v] = id;
    }

    return { labels, palette, maxLabelId: nextId - 1 };
}
